import { createBrowserRouter } from 'react-router-dom';
import { devRoutes } from 'virtual:efs-dev-routes';

import AppShell from './AppShell';
import BatchScopedRedirect from './BatchScopedRedirect';
import BatchShortcutPage from './BatchShortcutPage';
import RequireAuth from './RequireAuth';
import RouteGuard from './RouteGuard';
import { PER_BATCH_PATHS, type PerBatchPath } from '@/lib/batch-routes';
import LoginPage from '@/features/auth/LoginPage';
import ForbiddenPage from '@/features/ForbiddenPage';
import DashboardPage from '@/features/dashboard/DashboardPage';
import BlockerDashboardPage from '@/features/dashboard/BlockerDashboardPage';
import MonthlyBatchesPage from '@/features/monthly-batches/MonthlyBatchesPage';
import BatchDetailPage from '@/features/batches/detail/BatchDetailPage';
import OpeningBalancePage from '@/features/batches/opening-balance/OpeningBalancePage';
import ImportWizardPage from '@/features/imports/ImportWizardPage';
import ImportReviewPage from '@/features/imports/ImportReviewPage';
import InboundPage from '@/features/operations/inbound/InboundPage';
import InboundLotDetailPage from '@/features/operations/inbound/InboundLotDetailPage';
import SortingPage from '@/features/operations/sorting/SortingPage';
import DismantlingPage from '@/features/operations/dismantling/DismantlingPage';
import InventoryPage from '@/features/operations/inventory/InventoryPage';
import EvidencePage from '@/features/evidence/EvidencePage';
import FileLibraryPage from '@/features/file-library/FileLibraryPage';
import MassBalancePage from '@/features/mass-balance/MassBalancePage';
import AuditPackagePage from '@/features/audit-package/AuditPackagePage';
import IncomingItemsListPage from '@/features/masters/incoming-items/IncomingItemsListPage';
import MaterialsListPage from '@/features/masters/materials/MaterialsListPage';
import DestinationsListPage from '@/features/masters/destinations/DestinationsListPage';
import PackageTypesPage from '@/features/masters/package-types/PackageTypesPage';
import DismantlingMethodsPage from '@/features/masters/dismantling-methods/DismantlingMethodsPage';
import WarehousesPage from '@/features/masters/warehouses/WarehousesPage';
import SuppliersPage from '@/features/masters/suppliers/SuppliersPage';
import PriceReferencesPage from '@/features/pricing/references/PriceReferencesPage';
import UpsScenariosPage from '@/features/pricing/ups-scenarios/UpsScenariosPage';
import ApprovalsPage from '@/features/approvals/ApprovalsPage';
import ExceptionsPage from '@/features/exceptions/ExceptionsPage';
import UsersPage from '@/features/settings/users/UsersPage';
import RolesPage from '@/features/settings/roles/RolesPage';
import RoleEditPage from '@/features/settings/roles/RoleEditPage';
import WorkPackagesPage from '@/features/settings/work-packages/WorkPackagesPage';
import WorkPackageEditPage from '@/features/settings/work-packages/WorkPackageEditPage';
import CompaniesPage from '@/features/settings/companies/CompaniesPage';
import FacilitiesPage from '@/features/settings/facilities/FacilitiesPage';
import MyAccountPage from '@/features/settings/my-account/MyAccountPage';
import DispatchWorkspacePage from '@/features/dispatch/DispatchWorkspacePage';
import FirstValuationPage from '@/features/valuation/first-valuation';
import MonthlyMaterialValuationPage from '@/features/valuation/monthly-material';
import GetitPurchasePricePage from '@/features/valuation/getit-purchase-price';
import EfDkSettlementPage from '@/features/settlement/ef-dk';
import MonthlyReportsPage from '@/features/reports/monthly';
import WeightDifferencePage from '@/features/operations/weight-difference';
import OutboundsPage from '@/features/operations/outbounds';
import ReclassifiedReceiptsPage from '@/features/operations/reclassified-receipts';
import CorrectionRequestsPage from '@/features/governance/corrections';
import SupplierBatchesPage from '@/features/supplier-portal/SupplierBatchesPage';
import SupplierBatchDetailPage from '@/features/supplier-portal/SupplierBatchDetailPage';
import SupplierQuotationPage from '@/features/valuation/supplier-quotation';
import BuyerProposalsPage from '@/features/operations/buyer-proposals';
import NotFoundPage from '@/features/NotFoundPage';

const batchShortcut = (path: PerBatchPath) => (
  <BatchShortcutPage template={PER_BATCH_PATHS[path]} />
);

/**
 * Route tree mirrors docs/specs/06-ui-api-spec.md §2.
 *
 * Phase 2 introduces an authenticated outer gate (`RequireAuth`) that wraps
 * the `AppShell`. `/login` lives outside the gate and is the only
 * non-authenticated route.
 *
 * 22 placeholder routes (under the auth gate):
 *   /, /batches, /batches/:batchId, /batches/:batchId/opening-balance,
 *   /batches/:batchId/imports, /batches/:batchId/imports/:jobId,
 *   /batches/:batchId/inbound, /batches/:batchId/sorting,
 *   /batches/:batchId/dismantling, /batches/:batchId/secondary-process,
 *   /batches/:batchId/inventory, /batches/:batchId/shipments,
 *   /batches/:batchId/evidence, /batches/:batchId/mass-balance,
 *   /batches/:batchId/audit-package,
 *   /masters/incoming-items, /masters/materials, /masters/destinations,
 *   /pricing/references, /pricing/ups-scenarios,
 *   /approvals, /exceptions, /settings/users.
 *
 * Note: `/imports` is added as a top-level shortcut for the sidebar entry;
 * it is not in 06-ui-api §2 but is required by the sidebar navigation in
 * 08-pa-jp-design-reference §5.
 */
/**
 * DEV-ONLY routes (spec-13 §16.4). Sourced
 * from the `virtual:efs-dev-routes` module: the real registry under dev/test,
 * an empty registry in `vite build` — so nothing from the dev directory can
 * reach a production bundle (guarded by `scripts/build-leak-audit.mjs`).
 */
export const router = createBrowserRouter([
  ...(import.meta.env.DEV ? devRoutes() : []),
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    element: <RequireAuth />,
    errorElement: <NotFoundPage />,
    children: [
      {
        path: '/',
        element: <AppShell />,
        children: [
          // 403 page — must be reachable even if RouteGuard would block it.
          { path: 'forbidden', element: <ForbiddenPage /> },

          // Everything else passes through RouteGuard, which checks the
          // role-access table and redirects on failure.
          {
            element: <RouteGuard />,
            children: [
              // Dashboard
              { index: true, element: <DashboardPage /> },
              { path: 'blockers', element: <BlockerDashboardPage /> },

              // Monthly Batches
              { path: 'batches', element: <MonthlyBatchesPage /> },
              { path: 'batches/:batchId', element: <BatchDetailPage /> },
              { path: 'batches/:batchId/opening-balance', element: <OpeningBalancePage /> },
              { path: 'batches/:batchId/imports', element: <ImportWizardPage /> },
              { path: 'batches/:batchId/imports/:jobId', element: <ImportReviewPage /> },
              { path: 'batches/:batchId/inbound', element: <InboundPage /> },
              { path: 'batches/:batchId/sorting', element: <SortingPage /> },
              { path: 'batches/:batchId/dismantling', element: <DismantlingPage /> },
              {
                path: 'batches/:batchId/secondary-process',
                element: <BatchScopedRedirect suffix="outbounds" />,
              },
              { path: 'batches/:batchId/inventory', element: <InventoryPage /> },
              {
                path: 'batches/:batchId/shipments',
                element: <BatchScopedRedirect suffix="outbounds" />,
              },
              { path: 'batches/:batchId/outbounds', element: <OutboundsPage /> },
              { path: 'batches/:batchId/evidence', element: <EvidencePage /> },
              { path: 'batches/:batchId/mass-balance', element: <MassBalancePage /> },
              { path: 'batches/:batchId/audit-package', element: <AuditPackagePage /> },
              { path: 'batches/:batchId/exceptions', element: <ExceptionsPage /> },
              { path: 'batches/:batchId/approvals', element: <ApprovalsPage /> },

              // Financial spine (valuation / settlement / reports) — batch-scoped
              { path: 'batches/:batchId/first-valuation', element: <FirstValuationPage /> },
              {
                path: 'batches/:batchId/valuation/monthly-material',
                element: <MonthlyMaterialValuationPage />,
              },
              {
                path: 'batches/:batchId/valuation/supplier-purchase-price',
                element: <GetitPurchasePricePage />,
              },
              {
                path: 'batches/:batchId/valuation/getit-purchase-price',
                element: <GetitPurchasePricePage />,
              },
              { path: 'batches/:batchId/settlement', element: <EfDkSettlementPage /> },
              { path: 'batches/:batchId/reports', element: <MonthlyReportsPage /> },
              {
                path: 'batches/:batchId/supplier-quotation',
                element: <SupplierQuotationPage />,
              },
              {
                path: 'batches/:batchId/buyer-proposals',
                element: <BuyerProposalsPage />,
              },

              // Phase 2–4 operations (batch-scoped)
              {
                path: 'batches/:batchId/weight-difference',
                element: <WeightDifferencePage />,
              },
              {
                path: 'batches/:batchId/disassembly-batches',
                element: <BatchScopedRedirect suffix="dismantling" />,
              },
              {
                path: 'batches/:batchId/sales-outbounds',
                element: <BatchScopedRedirect suffix="outbounds" />,
              },
              // Reclassified receipts are keyed by a disassembly_batch id (the
              // page reads `:batchId` as the disassembly_batch). Reached via a
              // row action on the disassembly-batches list.
              {
                path: 'disassembly-batches/:batchId/reclassified-receipts',
                element: <ReclassifiedReceiptsPage />,
              },

              // Governance — correction requests (top-level)
              { path: 'corrections', element: <CorrectionRequestsPage /> },

              // Cross-batch / global shortcuts (sidebar entries from 08-pa-jp §5)
              { path: 'operations/inbound', element: <InboundPage /> },
              { path: 'operations/sorting', element: <SortingPage /> },
              { path: 'operations/dismantling', element: <DismantlingPage /> },
              {
                path: 'operations/secondary-process',
                element: batchShortcut('/operations/secondary-process'),
              },
              { path: 'operations/inventory', element: <InventoryPage /> },
              {
                path: 'operations/shipments',
                element: batchShortcut('/operations/shipments'),
              },
              {
                path: 'operations/weight-difference',
                element: batchShortcut('/operations/weight-difference'),
              },
              {
                path: 'operations/disassembly-batches',
                element: batchShortcut('/operations/disassembly-batches'),
              },
              {
                path: 'operations/sales-outbounds',
                element: batchShortcut('/operations/sales-outbounds'),
              },
              {
                path: 'operations/outbounds',
                element: batchShortcut('/operations/outbounds'),
              },
              { path: 'operations/dispatch', element: <DispatchWorkspacePage /> },
              { path: 'valuation/first', element: batchShortcut('/valuation/first') },
              {
                path: 'valuation/monthly-material',
                element: batchShortcut('/valuation/monthly-material'),
              },
              {
                path: 'valuation/supplier-purchase-price',
                element: batchShortcut('/valuation/supplier-purchase-price'),
              },
              {
                path: 'valuation/getit-purchase-price',
                element: batchShortcut('/valuation/getit-purchase-price'),
              },
              {
                path: 'valuation/supplier-quotation',
                element: batchShortcut('/valuation/supplier-quotation'),
              },
              {
                path: 'operations/buyer-proposals',
                element: batchShortcut('/operations/buyer-proposals'),
              },
              { path: 'settlement', element: batchShortcut('/settlement') },
              { path: 'reports/monthly', element: batchShortcut('/reports/monthly') },
              { path: 'evidence/library', element: <FileLibraryPage /> },
              { path: 'evidence', element: <EvidencePage /> },
              { path: 'mass-balance', element: <MassBalancePage /> },
              { path: 'audit-package', element: <AuditPackagePage /> },

              // Masters
              { path: 'masters/incoming-items', element: <IncomingItemsListPage /> },
              { path: 'masters/materials', element: <MaterialsListPage /> },
              { path: 'masters/destinations', element: <DestinationsListPage /> },
              { path: 'masters/package-types', element: <PackageTypesPage /> },
              { path: 'masters/dismantling-methods', element: <DismantlingMethodsPage /> },
              { path: 'masters/warehouses', element: <WarehousesPage /> },
              { path: 'masters/suppliers', element: <SuppliersPage /> },

              // Inbound lot detail
              { path: 'batches/:batchId/inbound/:lotId', element: <InboundLotDetailPage /> },

              // Pricing
              { path: 'pricing/references', element: <PriceReferencesPage /> },
              { path: 'pricing/ups-scenarios', element: <UpsScenariosPage /> },

              // Cross-batch lists
              { path: 'approvals', element: <ApprovalsPage /> },
              { path: 'exceptions', element: <ExceptionsPage /> },

              // Settings
              { path: 'settings/users', element: <UsersPage /> },
              { path: 'settings/roles', element: <RolesPage /> },
              { path: 'settings/roles/:roleCode/edit', element: <RoleEditPage /> },
              { path: 'settings/work-packages', element: <WorkPackagesPage /> },
              {
                path: 'settings/work-packages/:workPackageId/edit',
                element: <WorkPackageEditPage />,
              },
              { path: 'settings/companies', element: <CompaniesPage /> },
              { path: 'settings/facilities', element: <FacilitiesPage /> },
              { path: 'settings/my-account', element: <MyAccountPage /> },

              // Multi-tenant dispatch flow (added 2026-05-17)
              { path: 'dispatch', element: <DispatchWorkspacePage /> },
              { path: 'supplier/requests', element: <DispatchWorkspacePage /> },

              // Supplier portal (P0-N5): own cases, quotation, exception
              // replies, external report — all via party-scoped endpoints.
              { path: 'supplier/batches', element: <SupplierBatchesPage /> },
              { path: 'supplier/batches/:batchId', element: <SupplierBatchDetailPage /> },
              { path: 'platform/inbox', element: <DispatchWorkspacePage /> },
              { path: 'batches/:batchId/dispatch', element: <DispatchWorkspacePage /> },

              // 404
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
